import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuItemContent } from '../types.js';
import {
  FRESH_DRINKS_FAMILY,
  FRESH_DRINKS_FAMILY_NAME,
  FRESH_DRINKS_THEME,
  FRESH_DRINKS_VARIANTS,
  fdBrand,
  fdNames,
  fdPhoto,
  fdSharedCss,
  fdSizes,
  fdTitle,
} from './fresh-drinks-cards.js';
import { badge, bilingual, description, hasTitle, sectionTitle, type MenuTemplate, type RenderContext } from './common.js';

/**
 * Fresh Drinks, layout B: no side panel. A slim colour header with the ribbon title runs across
 * the top, and under it a grid of tall cards (four across in landscape, two in portrait): the
 * slanted name bar, the photo, then the sizes with dotted leaders.
 */

const S = '.mb-t-fresh-drinks-grid';

/** Cards per column: two rows in landscape, four in portrait. */
const ROWS = { landscape: 2, portrait: 4 };
const COLS = { landscape: 4, portrait: 2 };

function card(ctx: RenderContext, item: MenuItemContent, tag: string): string {
  const badges = badge(ctx, item, 'fdg-badge');
  return [
    '<div class="fdg-card">',
    tag,
    `<div class="fdg-bar">${fdNames(ctx, item, 'fdg')}${badges ? `<span class="fdg-badges">${badges}</span>` : ''}</div>`,
    fdPhoto(ctx, item, 'fdg'),
    `<div class="fdg-info">${fdSizes(ctx, item, 'fdg')}${description(ctx, item, 'fdg-desc')}</div>`,
    '</div>',
  ].join('');
}

/** A column of cards; the first card of a section carries its name on a tab. */
function column(ctx: RenderContext, blocks: MenuBlock[], rows: number): string {
  const cards: string[] = [];
  for (const b of blocks) {
    b.items.forEach((item, i) => {
      const tag =
        i === 0 && !b.continued && hasTitle(b.section) ? `<div class="fdg-tab">${sectionTitle(ctx, b.section, 'fdg-tab-t')}</div>` : '';
      cards.push(card(ctx, item, tag));
    });
  }
  return `<div class="fdg-col" style="--rows:${rows}">${cards.join('')}</div>`;
}

export const freshDrinksGrid: MenuTemplate = {
  id: 'fresh-drinks-grid',
  name: 'Fresh drinks · Card grid',
  description:
    'A slim colour header with the ribbon title over a grid of tall drink cards: slanted name bar, photo, and every size with dotted leaders.',
  defaultTheme: FRESH_DRINKS_THEME,
  variants: FRESH_DRINKS_VARIANTS,
  cuisines: ['drinks', 'cafe'],
  family: { id: FRESH_DRINKS_FAMILY, name: FRESH_DRINKS_FAMILY_NAME, layout: 'Card grid' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    // Every card is the same size, so a column holds a fixed number of them.
    const col: MenuColumn = { capacity: ROWS[orientation], blockCost: (_s, items) => items.length };
    return packColumns(groupBySection(board), Array.from({ length: COLS[orientation] }, () => col), { balance: true });
  },

  render(ctx) {
    const { board } = ctx;
    const header = [
      '<div class="fdg-header">',
      `<div class="fdg-side">${fdBrand(ctx, 'fdg')}</div>`,
      `<div class="fdg-mid">${fdTitle(ctx, 'fdg', 10)}</div>`,
      `<div class="fdg-side fdg-side-end">${bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'fdg-tag')}</div>`,
      '</div>',
    ].join('');
    const empty = ctx.page.columns.every((c) => c.length === 0);
    const rows = ROWS[ctx.orientation];
    const grid = empty
      ? '<div class="mb-empty">No drinks on this board yet</div>'
      : `<div class="fdg-grid" style="--cols:${ctx.page.columns.length}">${ctx.page.columns.map((c) => column(ctx, c, rows)).join('')}</div>`;
    return header + grid;
  },

  css: `
${S}{padding:0}
${fdSharedCss(S, 'fdg')}
${S} .fdg-header{position:relative;flex:none;display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:2em;
  background:var(--mb-primary);color:#fff;padding:1.2em 3em 1.4em;
  background-image:radial-gradient(ellipse at 50% 0%,rgba(255,255,255,.25),transparent 60%),
    repeating-linear-gradient(115deg,rgba(255,255,255,.05) 0 1.2em,transparent 1.2em 3em);
  box-shadow:0 .4em 0 var(--mb-accent),0 .9em 1.6em rgba(0,0,0,.18)}
${S}.mb-portrait .fdg-header{grid-template-columns:1fr;gap:.8em;padding:2em 2.4em 2.2em}
${S} .fdg-side{min-width:0;display:flex;align-items:center}
${S}.mb-portrait .fdg-side{justify-content:center;text-align:center}
${S} .fdg-side-end{justify-content:flex-end;text-align:end;flex-direction:column;align-items:flex-end}
${S}.mb-portrait .fdg-side-end{align-items:center;text-align:center}
${S} .fdg-brand{align-items:flex-start;text-align:start}
${S}.mb-portrait .fdg-brand{align-items:center;text-align:center}
${S} .fdg-logo{height:4.5em}
${S} .fdg-mid .fdg-ttl{flex-direction:row;align-items:center;gap:.6em}
${S}.mb-portrait .fdg-mid .fdg-ttl{flex-direction:column;gap:0}
${S} .fdg-ribbon{font-size:2.8em}
${S} .fdg-caps{font-size:5.6em}
${S}.mb-portrait .fdg-caps:not(.mb-2nd){font-size:6.5em}
${S} .fdg-caps.mb-2nd{font-size:2.4em;align-self:center;margin:0}
${S} .fdg-tag{font-size:1.15em;line-height:1.35;opacity:.95;max-width:22em;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .fdg-tag.mb-2nd{font-size:1.05em}
${S} .fdg-grid{flex:1;min-height:0;display:grid;grid-template-columns:repeat(var(--cols),minmax(0,1fr));gap:1.6em;
  padding:2.6em 3em 2.4em}
${S}.mb-portrait .fdg-grid{padding:2.6em 2.4em 3em}
${S} .fdg-col{min-width:0;min-height:0;display:grid;grid-template-rows:repeat(var(--rows),minmax(0,1fr));gap:1.6em}
${S} .fdg-card{position:relative;min-height:0;display:flex;flex-direction:column;background:var(--mb-surface);border-radius:.5em;
  box-shadow:0 .35em 1em rgba(0,0,0,.1)}
${S} .fdg-tab{position:absolute;z-index:2;top:-1.1em;inset-inline-end:1em;background:var(--mb-accent);color:var(--mb-text);
  padding:.15em .9em;border-radius:99em;box-shadow:0 .2em .5em rgba(0,0,0,.18);display:flex;gap:.5em;align-items:baseline;max-width:80%}
${S} .fdg-tab-t{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);text-transform:uppercase;font-size:1.05em;
  letter-spacing:.06em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .fdg-tab-t.mb-2nd{letter-spacing:0;font-size:.95em}
${S} .fdg-bar{flex:none;display:flex;align-items:center;flex-wrap:wrap;gap:0 .6em;background:var(--mb-primary);color:#fff;
  padding:.35em 2.2em .35em 1em;min-height:2.9em;margin-inline-end:2.2em;border-radius:.5em 0 0 0;
  clip-path:polygon(0 0,100% 0,calc(100% - 1.3em) 100%,0 100%)}
${S}[dir=rtl] .fdg-bar{clip-path:polygon(0 0,100% 0,100% 100%,1.3em 100%);border-radius:0 .5em 0 0;padding:.35em 1em .35em 2.2em}
${S} .fdg-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.6em;text-transform:uppercase;
  line-height:1.1;letter-spacing:.02em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}
${S} .fdg-name2{font-family:var(--mb-font-heading);font-size:1.25em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}
${S} .fdg-ph{flex:1 1 0;min-height:4em;margin:.7em .7em 0;border-radius:.4em}
${S} .fdg-info{flex:none;padding:.6em 1.1em .8em;display:flex;flex-direction:column;gap:.25em}
${S}.mb-portrait .fdg-card{display:grid;grid-template-columns:minmax(0,1fr) 42%;grid-template-rows:auto 1fr}
${S}.mb-portrait .fdg-bar{grid-column:1 / span 2;grid-row:1}
${S}.mb-portrait .fdg-ph{grid-column:2;grid-row:2;margin:.7em;min-height:0}
${S}.mb-portrait .fdg-info{grid-column:1;grid-row:2;justify-content:center}
${S}.mb-portrait .fdg-sz{font-size:1.3em}
`,
};
